import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { AmountInput } from './components/loanRequestForm/AmountInput';
import { CurrencySelector } from './components/loanRequestForm/CurrencySelector';
import { PeriodSelector } from './components/loanRequestForm/PeriodSelector';
import { EmailSection } from './components/emailSection/EmailSection';
import { NavigationHeader } from './components/layout/NavigationHeader';
import { PoweredByFooter } from './components/layout/PoweredByFooter';
import { LoanTypeSection } from './components/loanTypeSection/LoanTypeSection';
import { PrimaryButton } from './components/ui/PrimaryButton';
import { useLoanRequestForm } from './hooks/useLoanRequestForm';
import { I18nProvider } from './providers/I18nProvider';
import styles from './App.module.less';

type Step = 1 | 2 | 3;

interface AppProps {
  currency?: string;
  currencyOptions?: string;
  partner?: string;
  email?: string;
  forwardParams?: Record<string, string>;
  locale?: string;
  amount?: string;
  focusAmount?: boolean;
  hidePoweredBy?: boolean;
  showTitle?: boolean;
  scale?: string;
}

function LoanRequestForm(props: AppProps) {
  const intl = useIntl();
  const { focusAmount, hidePoweredBy, showTitle, scale } = props;
  const parsedScale = scale ? parseFloat(scale) : undefined;
  const scaleValue = parsedScale && parsedScale > 0 && parsedScale <= 2 ? parsedScale : undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const setCqw = useCallback((el: HTMLDivElement) => {
    el.style.setProperty('--cqw', (el.offsetWidth / 100) + 'px');
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setCqw(el);
    const ro = new ResizeObserver(() => setCqw(el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [setCqw]);
  const {
    step,
    prevStep,
    direction,
    isAnimating,
    currency,
    amount,
    period,
    error,
    errorKey,
    loanType,
    customInterestRate,
    email,
    emailError,
    emailErrorKey,
    availableCurrencies,
    setPeriod,
    setLoanType,
    handleAmountChange,
    handleCurrencyChange,
    handleBack,
    handleCustomInterestRateChange,
    handleEmailChange,
    getCtaText,
    getCtaHandler,
    isCtaDisabled,
  } = useLoanRequestForm(props);

  const renderStepContent = (stepToRender: Step) => {
    if (stepToRender === 1) {
      return (
        <div className={styles.content}>
          {showTitle && (
            <h1 className={styles.title}>
              <span className={styles.titlePrimary}>
                {intl.formatMessage({ id: 'form.title.primary' })}
              </span>{' '}
              <span className={styles.titleMuted}>
                {intl.formatMessage({ id: 'form.title.muted' })}
              </span>
            </h1>
          )}
          <CurrencySelector
            selectedCurrency={currency}
            onCurrencyChange={handleCurrencyChange}
            availableCurrencies={availableCurrencies}
          />
          <div className={styles.middle}>
            <AmountInput
              currency={currency}
              value={amount}
              onChange={handleAmountChange}
              error={error}
              errorKey={errorKey}
              autoFocus={focusAmount}
              onSubmit={getCtaHandler()}
            />
            <PeriodSelector selectedPeriod={period} onPeriodChange={setPeriod} />
          </div>
        </div>
      );
    }

    if (stepToRender === 2) {
      return (
        <div className={styles.stepTwoContent}>
          <LoanTypeSection
            currency={currency}
            amount={amount}
            period={period}
            loanType={loanType}
            customInterestRate={customInterestRate}
            onLoanTypeChange={setLoanType}
            onCustomInterestRateChange={handleCustomInterestRateChange}
          />
        </div>
      );
    }

    return (
      <div className={styles.stepTwoContent}>
        <EmailSection
          loanType={loanType}
          email={email}
          onEmailChange={handleEmailChange}
          onSubmit={getCtaHandler()}
          currency={currency}
          amount={amount}
          period={period}
          customInterestRate={customInterestRate}
          error={emailError}
          errorKey={emailErrorKey}
        />
      </div>
    );
  };

  const renderSlideContent = () => {
    // When not animating, just render current step
    if (!isAnimating || prevStep === null) {
      return (
        <div className={styles.slideTrack}>
          <div className={styles.slidePanel}>
            {renderStepContent(step)}
          </div>
        </div>
      );
    }

    // During animation, render both panels with the appropriate animation
    const animationClass = direction === 'forward' ? styles.animateForward : styles.animateBack;

    return (
      <div className={`${styles.slideTrack} ${styles.slideTrackAnimating} ${animationClass}`}>
        <div className={styles.slidePanel}>
          {renderStepContent(direction === 'forward' ? prevStep : step)}
        </div>
        <div className={styles.slidePanel}>
          {renderStepContent(direction === 'forward' ? step : prevStep)}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={styles.widgetContainer}
      style={scaleValue ? { transform: `scale(${scaleValue})` } : undefined}
    >
      <div
        className={`${styles.headerWrapper} ${step === 1 ? styles.headerHidden : ''} ${step === 3 ? styles.headerWithBorder : ''}`}
      >
        <NavigationHeader
          currency={currency}
          amount={amount}
          period={period}
          onBack={handleBack}
        />
      </div>
      <div className={styles.slideContainer}>
        {renderSlideContent()}
      </div>
      <div className={`${styles.ctaContainer} ${step > 1 ? styles.ctaContainerWithBorder : ''} ${hidePoweredBy && step === 1 ? styles.ctaContainerNoFooter : ''}`}>
        <PrimaryButton
          fullWidth
          onClick={getCtaHandler()}
          disabled={isCtaDisabled}
        >
          {getCtaText()}
        </PrimaryButton>
      </div>
      {!hidePoweredBy && (
        <div className={`${styles.footerWrapper} ${step > 1 ? styles.footerHidden : ''}`}>
          <PoweredByFooter />
        </div>
      )}
    </div>
  );
}

export function App(props: AppProps) {
  return (
    <I18nProvider locale={props.locale}>
      <LoanRequestForm {...props} />
    </I18nProvider>
  );
}
